import React from "react";
import ReactDOM from "react-dom/client";
import { createBrowserRouter } from "@datadog/browser-rum-react/react-router-v6";
import { RouterProvider } from "react-router-dom";
import "./language/i18n";
import App from "./App";
import { ImageCacheProvider } from "./global/ImageCacheContext";
import { AuthProvider } from "./components/user/BCPAuthProvider";
import { HelmetProvider } from "react-helmet-async";
import { datadogRum } from "@datadog/browser-rum";
import { reactPlugin } from "@datadog/browser-rum-react";
import { ErrorBoundary } from "@datadog/browser-rum-react";
import { getEnvironmentVariable } from "./global/environmentVariables";
import { datadogLogs } from "@datadog/browser-logs";
import packageJson from "../package.json";
import { hasCookiePermission } from "./components/layout/cookies/CookieFunctions";
import { loadStylesheet } from "./styleSheets/StyleSheetFunctions";
import ErrorFallback from "./components/utilities/ErrorFallback";

if (typeof window !== "undefined" && !window.IntersectionObserver) {
  require("intersection-observer");
}

const amplifyBranch = getEnvironmentVariable("AMPLIFY_BRANCH");

// Disables RUM in dev / local testing
const disableAnalytics =
  window?.location?.hostname === "localhost" ||
  getEnvironmentVariable("IS_DEV") === "true";

const DDAppId = getEnvironmentVariable("DATADOG_APP_ID");
const DDClientToken = getEnvironmentVariable("DATADOG_CLIENT_TOKEN");
const DDConsent = hasCookiePermission("dataDog");
const sessionSampleRate = getEnvironmentVariable("DATADOG_SESSION_SAMPLE_RATE");
const sessionReplaySampleRate = getEnvironmentVariable("DATADOG_SESSION_REPLAY_SAMPLE_RATE");
const logsSampleRate = getEnvironmentVariable("DATADOG_LOGS_SAMPLE_RATE");

if (DDConsent && !disableAnalytics) {
  datadogRum.init({
    applicationId: DDAppId,
    clientToken: DDClientToken,
    site: "datadoghq.com",
    service: amplifyBranch,
    env: amplifyBranch,
    version: packageJson.version,
    sessionSampleRate: sessionSampleRate || 0,
    sessionReplaySampleRate: sessionReplaySampleRate || 0,
    defaultPrivacyLevel: "mask-user-input",
    plugins: [reactPlugin({ router: true })]
  });
}

datadogLogs.init({
  clientToken: DDClientToken,
  site: "datadoghq.com",
  service: amplifyBranch,
  forwardErrorsToLogs: true,
  sessionSampleRate: logsSampleRate,
  trackingConsent: DDConsent ? "granted" : "not-granted"
});

const router = createBrowserRouter([
  {
    path: "/*",
    element: (
      <ErrorBoundary fallback={ErrorFallback}>
        <ImageCacheProvider>
          <AuthProvider>
            <HelmetProvider>
              <App />
            </HelmetProvider>
          </AuthProvider>
        </ImageCacheProvider>
      </ErrorBoundary>
    )
  }
]);

function renderApp() {
  const root = ReactDOM.createRoot(document.getElementById("root"));

  root.render(
    <React.StrictMode>
      <RouterProvider router={router} />
    </React.StrictMode>
  );
}

const STYLESHEET = getEnvironmentVariable("STYLESHEET");

loadStylesheet(STYLESHEET, renderApp);
